import { formatBillingDate } from '@saaga/shared/utils/billing.util'
import { AlertTriangleIcon, Loader2Icon } from 'lucide-react'
import { type ReactNode, useEffect } from 'react'

import type { CurrentPlanSummary } from '@/components/billing/billing.interface'
import { BillingCurrentPlanCard } from '@/components/billing/billing-current-plan-card.organism'
import { BillingEarlyAccessPlanCard } from '@/components/billing/billing-early-access-plan-card.organism'
import { BillingRemoveSeatsWarning } from '@/components/billing/billing-remove-seats-warning.molecule'
import { BillingSubscriptionModal } from '@/components/billing/billing-subscription-modal.molecule'
import { EarlyAccessBanner } from '@/components/billing/early-access-banner.atom'
import { EmbeddedCheckoutDrawer } from '@/components/billing/embedded-checkout.organism'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { useEmbeddedCheckout } from '@/hooks/billing/use-embedded-checkout.hook'

import { useOrganizationEarlyAccess } from '@/hooks/use-organization-early-access.hook'
import { handleCheckoutReturnParams } from '@/utils/checkout-return.util'

import { useSubscriptionSeats } from './use-subscription-seats.hook'
import { useSubscriptionView } from './use-subscription-view.hook'

interface SubscriptionSettingsBodyProps {
  view: ReturnType<typeof useSubscriptionView>
  seats: ReturnType<typeof useSubscriptionSeats>
  checkoutState: ReturnType<typeof useEmbeddedCheckout>['checkoutState']
  closeCheckout: ReturnType<typeof useEmbeddedCheckout>['closeCheckout']
  handleCheckoutComplete: ReturnType<typeof useEmbeddedCheckout>['handleCheckoutComplete']
  isEarlyAccess: boolean
}

const SubscriptionSettingsBody: React.FC<SubscriptionSettingsBodyProps> = ({
  view,
  seats,
  checkoutState,
  closeCheckout,
  handleCheckoutComplete,
  isEarlyAccess,
}): ReactNode => (
  <div className="subscription-settings-view space-y-4">
    {!view.subscription.subscriptionsEnabled && (
      <EarlyAccessBanner className="subscription-early-access-alert" />
    )}
    {view.subscription.cancelAt && <CancellationAlert cancelAt={view.subscription.cancelAt} />}
    <SubscriptionContent
      currentPlanSummary={view.currentPlanSummary}
      isFreePlan={view.isFreePlan}
      seats={seats}
      subscriptionsEnabled={view.subscription.subscriptionsEnabled}
      earlyAccess={isEarlyAccess}
      onChangePlan={() => view.setIsModalOpen(true)}
    />
    <BillingSubscriptionModal
      isOpen={view.isModalOpen}
      onOpenChange={view.setIsModalOpen}
      planList={view.planList}
      onSelectPlan={view.handleSelectPlan}
      isLoading={view.isSubscribing}
      subscriptionsEnabled={view.subscription.subscriptionsEnabled}
      bannerMessage={
        !view.subscription.subscriptionsEnabled
          ? 'Subscriptions are temporarily unavailable during Early Access.'
          : undefined
      }
    />
    <BillingRemoveSeatsWarning
      isOpen={seats.isWarningOpen}
      onOpenChange={seats.setIsWarningOpen}
      currentMemberCount={seats.warningMemberCount}
      onGoToMembers={seats.handleGoToMembers}
    />
    <EmbeddedCheckoutDrawer
      isOpen={checkoutState.isOpen}
      onOpenChange={(isOpen) => {
        if (!isOpen) closeCheckout()
      }}
      clientSecret={checkoutState.clientSecret}
      onComplete={handleCheckoutComplete}
    />
  </div>
)

export const SubscriptionSettingsView: React.FC = (): ReactNode => {
  useEffect(() => {
    handleCheckoutReturnParams()
  }, [])
  const { checkoutState, openCheckout, closeCheckout, handleCheckoutComplete } =
    useEmbeddedCheckout()
  const view = useSubscriptionView((clientSecret) => {
    openCheckout(clientSecret)
  })
  const seats = useSubscriptionSeats()
  const { isEarlyAccess } = useOrganizationEarlyAccess()

  const isLoadingAll = view.isLoading || (!view.isFreePlan && seats.isLoadingSeats)

  if (isLoadingAll) return <LoadingState />
  if (view.error) return <ErrorState error={view.error} />

  return (
    <SubscriptionSettingsBody
      view={view}
      seats={seats}
      checkoutState={checkoutState}
      closeCheckout={closeCheckout}
      handleCheckoutComplete={handleCheckoutComplete}
      isEarlyAccess={isEarlyAccess}
    />
  )
}

interface SubscriptionContentProps {
  currentPlanSummary: CurrentPlanSummary | null
  isFreePlan: boolean
  seats: ReturnType<typeof useSubscriptionSeats>
  subscriptionsEnabled: boolean
  earlyAccess: boolean
  onChangePlan: () => void
}

const SubscriptionContent: React.FC<SubscriptionContentProps> = ({
  currentPlanSummary,
  isFreePlan,
  seats,
  subscriptionsEnabled,
  earlyAccess,
  onChangePlan,
}): ReactNode => {
  if (earlyAccess) {
    return (
      <BillingEarlyAccessPlanCard
        seatCount={{
          used: seats.seatInfo?.usedSeats ?? 0,
          allowed: seats.seatInfo?.totalSeats ?? 1,
        }}
        seatInfo={seats.seatInfo}
        isAddingSeats={seats.isAddingSeats}
        isRemovingSeats={seats.isRemovingSeats}
        onAddSeats={seats.handleAddSeats}
        onRemoveSeats={seats.handleRemoveSeats}
      />
    )
  }

  if (!currentPlanSummary) {
    if (!subscriptionsEnabled) return null
    return <NoPlanState onChoosePlan={onChangePlan} />
  }

  return (
    <BillingCurrentPlanCard
      summary={currentPlanSummary}
      onChangePlan={onChangePlan}
      seatInfo={isFreePlan ? null : seats.seatInfo}
      isAddingSeats={seats.isAddingSeats}
      isRemovingSeats={seats.isRemovingSeats}
      onAddSeats={seats.handleAddSeats}
      onRemoveSeats={seats.handleRemoveSeats}
    />
  )
}

const LoadingState: React.FC = (): ReactNode => (
  <div className="subscription-settings-view flex items-center justify-center py-12">
    <Loader2Icon className="size-8 animate-spin text-muted-foreground" />
  </div>
)

const ErrorState: React.FC<{ error: string }> = ({ error }): ReactNode => (
  <div className="subscription-settings-view">
    <p className="text-center text-destructive py-6">{error}</p>
  </div>
)

const CancellationAlert: React.FC<{ cancelAt: string }> = ({ cancelAt }): ReactNode => (
  <Alert variant="destructive" className="subscription-cancellation-alert">
    <AlertTriangleIcon className="size-4" />
    <AlertTitle>Subscription cancelling</AlertTitle>
    <AlertDescription>
      Your subscription will be cancelled on {formatBillingDate(new Date(cancelAt))}. You will lose
      access to paid features after this date.
    </AlertDescription>
  </Alert>
)

const NoPlanState: React.FC<{ onChoosePlan: () => void }> = ({ onChoosePlan }): ReactNode => (
  <div className="text-center py-6">
    <p className="text-muted-foreground mb-4">No active subscription</p>
    <button type="button" className="text-primary hover:underline" onClick={onChoosePlan}>
      Choose a plan
    </button>
  </div>
)

SubscriptionSettingsView.displayName = 'SubscriptionSettingsView'
